import * as React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Box,
  Typography as MUITypography,
  styled,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import { DataGrid, gridClasses, esES } from '@mui/x-data-grid'
import {
  fetchCantonTenantRelations,
  deleteCantonTenantRelation,
} from '../../../services/cantonTenantService'
import { reassignTreesForTenant } from '../../../services/tenantService'
import { setIsStored } from '../../../redux/slices/Alerts'
import { grey } from '@mui/material/colors'
import { getColumns } from './columns'
import Botton from '../../BottonCustom'
import { Create } from '@mui/icons-material'
import CantonTenantFormModal from './FormModal'
import AlertCustom from '../../Alerts'

const styleContainer = { height: '88vh', width: '100%' }
const styleDefault = theme => {
  const primary = `var(--color-primary, ${theme.palette.primary.main})`

  return {
    color: primary,
    border: `1px solid ${primary}`,
    width: '100%',
    fontSize: '12px',
    '&:hover': {
      backgroundColor: primary,
      borderColor: primary,
      color: theme.palette.common.white,
    },
    '&:active': { boxShadow: 'none', opacity: 0.9 },
  }
}

const styleHeaderTab = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignContent: 'center',
  alignItems: 'baseline',
  mx: 4,
  mb: 1,
}

const Typography = styled(MUITypography)(props => ({
  fontFamily: props.theme.typography.fontFamily,
  fontStyle: 'normal',
  fontSize: '1em',
  fontWeight: 650,
  lineHeight: '18px',
  color: '#6A6A6A',
  paddingBottom: 6,
  paddingTop: 6,
  textAlign: 'center',
  marginTop: 12,
  marginBottom: 3,
  height: '5%',
}))

const CantonTenantTable = () => {
  const modalRef = React.useRef()
  const alertRef = React.useRef()
  const dispatch = useDispatch()

  const [data, setData] = React.useState([])
  const [pageSize, setPageSize] = React.useState(25)
  const [loading, setLoading] = React.useState(true)
  const [selectedRow, setSelectedRow] = React.useState(null)

  // Dialog state — eliminar
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [rowToDelete, setRowToDelete] = React.useState(null)

  // Dialog state — reasignar árboles
  const [reassignDialogOpen, setReassignDialogOpen] = React.useState(false)
  const [rowToReassign, setRowToReassign] = React.useState(null)

  const { isStored } = useSelector(state => state.alertsGlobal)

  const loadData = React.useCallback(async () => {
    setLoading(true)
    try {
      const relations = await fetchCantonTenantRelations()
      setData(relations)
    } catch (err) {
      console.error('Error loading relations:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleEdit = row => {
    setSelectedRow(row)
    if (modalRef.current) {
      modalRef.current.openModal()
    }
  }

  const handleDeleteClick = React.useCallback(row => {
    setRowToDelete(row)
    setDeleteDialogOpen(true)
  }, [])

  const confirmDelete = async () => {
    if (!rowToDelete) return
    setLoading(true)
    setDeleteDialogOpen(false)
    try {
      const response = await deleteCantonTenantRelation(rowToDelete.id)
      if (response.success) {
        dispatch(
          setIsStored({ stored: true, message: 'Asociación eliminada correctamente' }),
        )
        loadData()
      } else {
        dispatch(setIsStored({ stored: false, message: response.message }))
      }
    } catch (error) {
      dispatch(setIsStored({ stored: false, message: 'Error al eliminar la asociación' }))
    } finally {
      setLoading(false)
      setRowToDelete(null)
    }
  }

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false)
    setRowToDelete(null)
  }

  const handleReassignClick = React.useCallback(row => {
    setRowToReassign(row)
    setReassignDialogOpen(true)
  }, [])

  const confirmReassign = async () => {
    if (!rowToReassign) return
    setReassignDialogOpen(false)
    setLoading(true)
    try {
      const result = await reassignTreesForTenant(rowToReassign.tenant.id)
      if (result.success) {
        dispatch(setIsStored({ stored: true, message: result.data.message }))
        loadData()
      } else {
        dispatch(setIsStored({ stored: false, message: result.message }))
      }
    } catch (error) {
      dispatch(setIsStored({ stored: false, message: 'Error inesperado al reasignar árboles' }))
    } finally {
      setLoading(false)
      setRowToReassign(null)
    }
  }

  const handleCancelReassign = () => {
    setReassignDialogOpen(false)
    setRowToReassign(null)
  }

  const columns = React.useMemo(
    () => getColumns(handleEdit, handleDeleteClick, loadData, handleReassignClick),
    [handleDeleteClick, handleReassignClick, loadData],
  )

  const handleFormCreation = () => {
    setSelectedRow(null) // Reset for create mode
    if (modalRef.current) {
      modalRef.current.openModal()
    }
  }

  const handleModalClose = () => {
    setSelectedRow(null)
  }

  React.useEffect(() => {
    loadData()
  }, [loadData])

  React.useEffect(() => {
    if (!Boolean(isStored)) return
    alertRef?.current?.openAlert(true)
  }, [isStored])

  return (
    <Box sx={styleContainer}>
      <Box sx={styleHeaderTab}>
        <Box>
          <Typography>Asociaciones Cantón-Tenant</Typography>
        </Box>
        <Box>
          <Botton
            text="Nueva Asociación"
            onClick={handleFormCreation}
            startIcon={<Create />}
            styleButton={styleDefault}
            size="small"
          />
        </Box>
      </Box>
      <div style={{ height: '95%', width: '100%' }}>
        <DataGrid
          rows={data}
          columns={columns}
          pageSize={pageSize}
          loading={loading}
          getRowId={row => row.id}
          rowsPerPageOptions={[25, 50, 75]}
          onPageSizeChange={newPageSize => setPageSize(newPageSize)}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          getRowSpacing={params => ({
            top: params.isFirstVisible ? 0 : 5,
            bottom: params.isLastVisible ? 0 : 5,
          })}
          sx={{
            ml: 2,
            [`& .${gridClasses.row}`]: {
              bgcolor: grey[100],
            },
          }}
          disableSelectionOnClick
        />
      </div>
      <CantonTenantFormModal
        ref={modalRef}
        initialValues={selectedRow}
        onClose={handleModalClose}
        onSuccess={() => {
          loadData()
          dispatch(setIsStored({ stored: true, message: 'Guardado exitosamente' }))
        }}
      />
      <AlertCustom
        ref={alertRef}
        severity={isStored?.stored !== true ? 'warning' : 'success'}>
        {isStored?.message ||
          (isStored?.stored !== true ? 'Error desconocido' : 'Guardado exitosamente.')}
      </AlertCustom>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{'¿Estás seguro?'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {rowToDelete
              ? `Se eliminará la asociación entre ${rowToDelete.canton?.name || 'Cantón'} y ${rowToDelete.tenant?.name || 'Tenant'}.`
              : 'Confirmar eliminación'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancelar</Button>
          <Button onClick={confirmDelete} color="error" autoFocus>
            Sí, eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reassign Trees Confirmation Dialog */}
      <Dialog
        open={reassignDialogOpen}
        onClose={handleCancelReassign}
        aria-labelledby="reassign-dialog-title"
        aria-describedby="reassign-dialog-description">
        <DialogTitle id="reassign-dialog-title">¿Reasignar árboles?</DialogTitle>
        <DialogContent>
          <DialogContentText id="reassign-dialog-description">
            {rowToReassign
              ? `Se buscarán todos los árboles dentro del boundary_geojson del tenant "${rowToReassign.tenant?.name || ''}" y se les asignará este tenant. Los árboles sin coordenadas serán omitidos.`
              : 'Confirmar reasignación'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReassign}>Cancelar</Button>
          <Button onClick={confirmReassign} color="primary" autoFocus>
            Sí, reasignar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CantonTenantTable
